import React, { CSSProperties, useState, useMemo, useEffect } from "react";
import { Accordion, Button, Icon, Input, Modal } from "semantic-ui-react";

// UX
import CardSelectPackageUX from "./CardSelectPackageUX";
import CardPurchaseHistory from "./CardPurchaseHistory";
import CardAppointmentPackage from "./CardAppointmentPackage";

// Common
import ModSearchDoctorNote from "react-lib/apps/APP/ModSearchDoctorNote";
import ModConfirmAuthen from "react-lib/apps/common/ModConfirmAuthen";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Interface
import {
  AppointmentPackageSerializer,
  MasterOptionsType,
  PackageItemDetailType,
  PackageItemSerializer,
  PickedProps,
  RunSequence,
  SEQ_ACTIONS,
  SetProp,
  State,
} from "./sequence/PackageDateTime";

// Utils
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import { useIntl } from "react-intl";

type CardSelectPackageProps = {
  setProp: SetProp;
  onEvent: (e: any) => any;
  // data
  selectedPatient?: Record<string, any> | null;
  apiToken?: string | null;
  division?: number | string | null;
  // controller
  proxyController?: any;
  // seq
  runSequence: RunSequence;
  PackagePurchaseSequence?: any;
  PackageDateTimeSequence?: State["PackageDateTimeSequence"];
  // options
  masterOptions?: MasterOptionsType;
  // CommonInterface
} & PickedProps;

// Const
const GridCenter = {
  display: "grid",
  placeContent: "center",
  height: "fit-content",
} as CSSProperties;

const CARD_SELECT_PACKAGE = "CardSelectPackage";

const ACTION_CANCEL = `${CARD_SELECT_PACKAGE}_${SEQ_ACTIONS.CANCEL}`;

const CardSelectPackage = (props: CardSelectPackageProps) => {
  const intl = useIntl();
  // data
  const [activeIndex, setActiveIndex] = useState<number[]>([]);
  // Mod
  const [openModSelectPackage, setOpenModSelectPackage] =
    useState<boolean>(false);
  const [modEdit, setModEdit] = useState<{
    index: number;
    data: PackageItemSerializer;
  } | null>(null);
  const [openModDoctorNote, setOpenModDoctorNote] = useState<boolean>(false);
  // ModConfirm
  const [questionDetail, setQuestionDetail] = useState<any>(null);
  const [openModMessage, setOpenModMessage] = useState<boolean>(false);
  const [openModCancel, setOpenModCancel] = useState<boolean>(false);
  const [modDelete, setModDelete] = useState<number | null>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [currentValue, setCurrentValue] = useState<any[]>([]);

  const [reason, setReason] = useState<any>("");

  // Use Effect
  useEffect(() => {
    if (!props.PackageDateTimeSequence?.sequenceIndex) {
      props.runSequence({
        sequence: "PackageDateTime",
        restart: true,
        contentId: props.selectedAppointment?.content_id,
        card: CARD_SELECT_PACKAGE,
      });

      setActiveIndex([])
    }

    return () => {
      props.setProp(`buttonLoadCheck.${CARD_SELECT_PACKAGE}`, null);
    };
  }, [props.selectedAppointment]);

  // Use Memo
  const appointmentPackageDetail = useMemo(() => {
    return props.PackageDateTimeSequence?.appointmentPackageDetail;
  }, [props.PackageDateTimeSequence?.appointmentPackageDetail]);

  const slotDate = useMemo(() => {
    const detail = appointmentPackageDetail;
    const date = detail?.appointment_thai_date;

    return date ? `วัน ${date}` : "";
  }, [appointmentPackageDetail]);

  const slotTime = useMemo(() => {
    const detail = appointmentPackageDetail;
    const startTime = detail?.start_time;
    const endTime = detail?.end_time;

    return startTime ? `${startTime}-${endTime}` : "";
  }, [appointmentPackageDetail]);

  const selectedPackageItems = useMemo(() => {
    return props.PackageDateTimeSequence?.selectedPackageItems || [];
  }, [props.PackageDateTimeSequence?.selectedPackageItems]);

  const packageOrderIds = useMemo(() => {
    return (props.PackageDateTimeSequence?.selectedPackageItems || []).map(
      (item) => item.id
    );
  }, [props.PackageDateTimeSequence?.selectedPackageItems]);

  const orderDivisionName = useMemo(() => {
    return props.masterOptions?.division?.find(
      (option) => appointmentPackageDetail?.order_division === option.value
    )?.text;
  }, [props.masterOptions, appointmentPackageDetail]);

  const isLoadingSuccess = useMemo(() => {
    return props.buttonLoadCheck?.[CARD_SELECT_PACKAGE] === "SUCCESS";
  }, [props.buttonLoadCheck]);

  const orderNote = useMemo(() => {
    return (
      props.selectedAppointment?.order_note ||
      appointmentPackageDetail?.doctor_note ||
      ""
    );
  }, [appointmentPackageDetail, props.selectedAppointment]);

  // Handler
  const handleChangeValue = (e: any, data: any) => {
    const name = data.name as keyof AppointmentPackageSerializer;
    const detail = {
      ...(appointmentPackageDetail || {}),
    };

    detail[name] = data.value;

    props.setProp(`PackageDateTimeSequence.appointmentPackageDetail`, {
      ...detail,
    });
  };

  const handleClickAddPackage = () => {
    setOpenModSelectPackage(true);
  };

  const handleCloseModSelectPackage = () => {
    setOpenModSelectPackage(false);
  };

  const handleClick = (e: any, data: any) => {
    const index = Number(data.index);
    const newIndex = activeIndex.includes(data.index)
      ? activeIndex.filter((idx) => index !== idx)
      : [...activeIndex, index];

    setActiveIndex(newIndex);
  };

  const handleCloseModEdit = () => {
    setModEdit(null);
  };

  const handleDelete = (index: number) => () => {
    setModDelete(index);
  };

  const handleAddNote = () => {
    setOpenModDoctorNote(true);
  };

  const handleOnSelectNote = (noteList: string[]) => {
    const orderNote = props.selectedAppointment?.remark || "";

    const note: (string | number)[] = orderNote
      ? [orderNote, ...noteList]
      : noteList;

    props.setProp("selectedAppointment.remark", note.join("\n"));
  };

  const handlePostPoneAppointment = () => {
    setOpenModMessage(true);
    setOptions(props.masterOptions?.postponeAppointment || []);
    setQuestionDetail({
      type: "postpone",
      title: "ต้องการเลื่อนนัดผู้ป่วยใช่หรือไม่",
      subTitle:
        "กรณีที่เลื่อนนัดผู้ป่วยเจ้าหน้าที่จะต้องทำการติดนามนัดหมายผู้ป่วยใหม่",
      questionDetail: "ระบุเหตุผลเลื่อนนัดผู้ป่วย",
      isRequiredQuestionDetail: true,
      titleColor: "orange",
      userEditQuestion: "ระบุชื่อผู้บันทึกข้อมูล",
      isUserEditQuesion: true,
      buttonLeftLabel: "ใช่",
      buttonRightLabel: "ไม่",
      buttonLeftColor: "green",
      buttonRightColor: "red",
    });
  };

  const handleConfirmedButton = () => {
    if (questionDetail?.type === "postpone") {
      props.onEvent({
        message: "SetScheduling",
        params: {
          action: "PostponeAppointment",
          reason: currentValue,
          noGetWaiting: true,
        },
      });
    }

    setCurrentValue([]);
    setOpenModMessage(false);
    setQuestionDetail(null);
  };

  const handleCancelButton = () => {
    setOpenModMessage(false);
    setOptions([]);
    setCurrentValue([]);
    setQuestionDetail(null);
    props.setProp("userTokenize", null);
  };

  const handleChange = (e: any, { value }: any) => {
    setCurrentValue(value);
  };

  const handleAddition = (e: any, { value }: any) => {
    setOptions([{ text: value, value }, ...options]);
  };

  const handleConfirmCancel = () => {
    props.runSequence({
      sequence: "PackageDateTime",
      action: SEQ_ACTIONS.CANCEL,
      statusNote: reason,
      card: CARD_SELECT_PACKAGE,
      onSuccess: handleCloseModCancel,
    });
  };

  const handleCloseModCancel = () => {
    setOpenModCancel(false);
    setReason("");
  };

  const handleCancelAppointment = () => {
    setOpenModCancel(true);
  };

  const handleAddPackage = (data: any) => {
    const items = [...selectedPackageItems, data];

    props.setProp("PackageDateTimeSequence.selectedPackageItems", items);

    handleClick(null, { index: items.length - 1 });

    handleCloseModSelectPackage();
  };

  const handleEditPackage = (data: any[]) => {
    if (modEdit) {
      const items = props.PackageDateTimeSequence?.selectedPackageItems || [];

      items[modEdit?.index].items = data;

      props.setProp("PackageDateTimeSequence.selectedPackageItems", [...items]);

      handleCloseModEdit();
    }
  };

  const handlePrintAppointment = () => {
    props.onEvent({
      message: "PrintScheduling",
      params: {
        form: "FormAppointmentDetail",
        data: {
          ...props.selectedAppointment,
          doctor_note: orderNote?.match(/^.*[^\n\r]/gm) || "",
        },
      },
    });
  };

  const handleConfirmDelete = () => {
    let items = props.PackageDateTimeSequence?.selectedPackageItems || [];

    items = items.filter((item, idx) => idx !== modDelete);

    setActiveIndex(activeIndex.filter((_, idx) => idx !== modDelete));

    props.setProp("PackageDateTimeSequence.selectedPackageItems", [...items]);

    handleCloseModDelete();
  };

  const handleCloseModDelete = () => {
    setModDelete(null);
  };

  return (
    <div style={{ padding: "10px" }}>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_SELECT_PACKAGE}`, null);
        }}
        error={props.errorMessage?.[CARD_SELECT_PACKAGE]}
        success={null}
        languageUX={props.languageUX}
      />

      <CardSelectPackageUX
        // data
        isNotFound={!selectedPackageItems.length}
        isLoadingSuccess={isLoadingSuccess}
        patientInfo={props.selectedPatient}
        appointmentPackageDetail={appointmentPackageDetail}
        orderNote={orderNote}
        orderDivisionName={orderDivisionName}
        slotDate={slotDate}
        slotTime={slotTime}
        // config
        isSelected={!!slotTime}
        // * เลื่อนนัดหมาย
        isPostpone={props.selectedAppointment?.status === 4}
        // callback
        onAddPackage={handleClickAddPackage}
        onAddNote={handleAddNote}
        onChangeValue={handleChangeValue}
        onPostponeAppointment={handlePostPoneAppointment}
        onCancelAppointment={handleCancelAppointment}
        onPrint={handlePrintAppointment}
        // Element
        Accordion={
          <Accordion>
            {selectedPackageItems.map((item, index) => (
              <>
                <Accordion.Title
                  active={activeIndex.includes(index)}
                  index={index}
                  style={{
                    color: "#0072bc",
                    // fontSize: "1.2rem",
                    ...(selectedPackageItems.length - 1 === index
                      ? {}
                      : {
                          borderBottom: activeIndex.includes(index)
                            ? ""
                            : "1px solid #2185D0",
                          paddingBottom: "1rem",
                        }),
                    paddingTop: "1rem",
                  }}
                  onClick={handleClick}
                >
                  <Icon name="dropdown" />
                  {[item.product_code]} {item.product_name}
                </Accordion.Title>
                <Accordion.Content
                  active={activeIndex.includes(index)}
                  style={
                    activeIndex.includes(index) &&
                    selectedPackageItems.length - 1 !== index
                      ? {
                          borderBottom: "1px solid #2185D0",
                          marginTop: "-0.5rem",
                        }
                      : {}
                  }
                >
                  <CardAccordionContent
                    data={item.items}
                    // config
                    readOnly={!!slotTime}
                    // callback
                    onEdit={() => setModEdit({ index, data: item })}
                    onDelete={handleDelete(index)}
                    languageUX={props.languageUX}
                  />
                </Accordion.Content>
              </>
            ))}
          </Accordion>
        }
        languageUX={props.languageUX}
      />

      <Modal
        open={openModSelectPackage}
        closeOnDimmerClick={true}
        style={{
          maxHeight: "94vh",
          overflowY: "auto",
          paddingBottom: "1rem",
          width: "auto",
          minWidth: "71rem",
        }}
        onClose={handleCloseModSelectPackage}
      >
        <Icon
          name="close"
          color="red"
          size="large"
          style={{ position: "absolute", right: 5, top: 8 }}
          onClick={handleCloseModSelectPackage}
        />
        <CardPurchaseHistory
          apiToken={props.apiToken}
          division={props.division}
          setProp={props.setProp as any}
          onEvent={props.onEvent}
          // seq
          runSequence={props.runSequence as any}
          PackagePurchaseSequence={props.PackagePurchaseSequence}
          // CommonInterface
          searchedItemListWithKey={props.searchedItemListWithKey}
          buttonLoadCheck={props.buttonLoadCheck}
          // config
          selectable={true}
          selectedDivision={props.selectedDivision}
          disabledOrderIds={packageOrderIds}
          statusesFilter={["BILLED", "PAID"]}
          // callback
          onSelectPackage={handleAddPackage}
          languageUX={props.languageUX}
        />
      </Modal>

      <Modal
        open={!!modEdit}
        closeOnDimmerClick={true}
        style={{ width: "auto" }}
        onClose={handleCloseModEdit}
      >
        <CardAppointmentPackage
          apiToken={props.apiToken}
          division={props.division}
          setProp={props.setProp as any}
          // data
          data={modEdit?.data?.items || []}
          headerName={`[${modEdit?.data?.product_name}] ${modEdit?.data?.product_name}`}
          usageLimit={modEdit?.data?.usage_limit}
          // config
          selectable={true}
          isEdit={true}
          hideHistory={true}
          // callback
          onClose={handleCloseModEdit}
          onSelect={handleEditPackage}
          languageUX={props.languageUX}
        />
      </Modal>

      <Modal
        open={openModDoctorNote}
        onClose={() => setOpenModDoctorNote(false)}
      >
        <ModSearchDoctorNote
          controller={props.proxyController}
          division={props.selectedDivision?.id}
          onClose={() => setOpenModDoctorNote(false)}
          onSelectNote={handleOnSelectNote}
          languageUX={props.languageUX}
        />
      </Modal>

      <ModConfirmAuthen
        {...questionDetail}
        onEvent={props.onEvent}
        setProp={props.setProp}
        // options
        options={options}
        userTokenize={props.userTokenize}
        onButtonLeftClick={handleConfirmedButton}
        onButtonRightClick={handleCancelButton}
        open={openModMessage}
        onClose={handleCancelButton}
        isButtonBasic={true}
        handleAddition={handleAddition}
        handleChange={handleChange}
        currentValue={currentValue}
        isErrMsg={true}
        multiple={questionDetail?.multiple}
        style={{ width: "40%" }}
      />

      <ModConfirm
        openModal={openModCancel}
        titleName={intl.formatMessage({ id: "ยกเลิกนัดหมาย" })}
        titleColor="red"
        size="mini"
        denyButtonColor="red"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        // approveButtonColor="green"
        // approveButtonText="ตกลง"
        approveButton={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleConfirmCancel}
            // data
            paramKey={ACTION_CANCEL}
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_CANCEL]}
            // config
            color={"green"}
            name={SEQ_ACTIONS.CANCEL}
            size="medium"
            title={intl.formatMessage({ id: "ยืนยัน" })}
            basic={true}
            disabled={!reason}
          />
        }
        content={
          <div style={{ margin: "-2rem 0px -1rem" }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ margin: "20px 0px 10px 0px" }}>
                กรุณาระบุเหตุผลในการยกเลิก
              </div>

              <Input
                value={reason}
                fluid={true}
                onChange={(e, data) => setReason(data.value)}
              />
            </div>
          </div>
        }
        onDeny={handleCloseModCancel}
        onCloseWithDimmerClick={handleCloseModCancel}
      />

      <ModConfirm
        openModal={typeof modDelete === "number"}
        titleName={intl.formatMessage({ id: "แจ้งเตือน" })}
        titleColor="red"
        size="mini"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        // callback
        onApprove={handleConfirmDelete}
        onDeny={handleCloseModDelete}
        onCloseWithDimmerClick={handleCloseModDelete}
        // ELement
        content={
          <div
            style={{
              margin: "0rem 1.5rem -1rem 0rem",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            ต้องการลบรายการใช่หรือไม่
          </div>
        }
      />
    </div>
  );
};

/* ------------------------------------------------------ */

/*                  CardAccordionContent;                 */

/* ------------------------------------------------------ */
type CardAccordionContentProps = {
  data: PackageItemDetailType[];
  // config
  readOnly?: boolean;
  // callback
  onEdit: (e: any) => any;
  onDelete: (e: any) => any;
};

const CardAccordionContent = (props: CardAccordionContentProps) => {
  const items = useMemo(() => {
    return props.data.filter((item) => item.active);
  }, [props.data]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto" }}>
      <div>
        {[
          {
            product_code: "รหัส",
            product_name: "รายการ",
            p_type_code: "ประเภท",
            quantity_appointment: "จำนวนนัดหมาย",
            balance: "จำนวนคงเหลือ",
          },
          ...items,
        ].map((item, index) => (
          <div
            key={"item" + item.product_code}
            style={{
              display: "grid",
              fontWeight: "normal",
              padding: "6px 25px 7px 10px",
              gridTemplateColumns: "repeat(5,20%)",
              ...(index === 0
                ? {
                    backgroundColor: "#d0eef2",
                    border: "1px solid #979797",
                  }
                : {}),
            }}
          >
            <div style={{ paddingLeft: index === 0 ? "1rem" : "" }}>
              {item.product_code}
            </div>
            <div style={index === 0 ? GridCenter : {}}>
              {" "}
              {item.product_name}
            </div>
            <div style={index === 0 ? GridCenter : { paddingLeft: "5rem" }}>
              {" "}
              {item.p_type_code}
            </div>
            <div style={GridCenter}> {item.quantity_appointment}</div>
            <div style={GridCenter}> {item.balance}</div>
          </div>
        ))}
      </div>
      <div>
        {!props.readOnly && (
          <Button
            color="yellow"
            icon="edit"
            size="mini"
            style={{ margin: "0.25rem", marginLeft: "0.5rem" }}
            onClick={props.onEdit}
          />
        )}
      </div>
      <div>
        {!props.readOnly && (
          <Button
            color="red"
            icon="trash alternate"
            size="mini"
            style={{ margin: "0.25rem", marginRight: 0 }}
            onClick={props.onDelete}
          />
        )}
      </div>
    </div>
  );
};

CardSelectPackage.displayName = "CardSelectPackage";

export default React.memo(CardSelectPackage);
